import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * The products a supplier sells (WP06): its own item code, the price it last
 * quoted, its lead time and whether it is the preferred source. One link per
 * supplier and variant, so saving again replaces it.
 */

export type SupplierProductLink = components['schemas']['SupplierProductResource']
export type SupplierProductBody = components['schemas']['SupplierProductRequest']

const key = (companyId: string | null, supplierId: string) =>
  ['companies', companyId, 'directory', 'suppliers', supplierId, 'products'] as const

export function useSupplierProducts(companyId: string | null, supplierId: string) {
  return useQuery({
    queryKey: key(companyId, supplierId),
    enabled: Boolean(companyId && supplierId),
    queryFn: async (): Promise<SupplierProductLink[]> => {
      const response = await api.GET('/directory/suppliers/{supplier}/products', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: { path: { supplier: supplierId } },
      })
      if (!response.data) throw new Error('GET supplier products returned no body')
      return response.data.data
    },
  })
}

export function useSaveSupplierProduct(companyId: string, supplierId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ variantId, body }: { variantId: string; body: SupplierProductBody }) => {
      const response = await api.PUT('/directory/suppliers/{supplier}/products/{variantId}', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { supplier: supplierId, variantId } },
        body,
      })
      if (!response.data) throw new Error('PUT supplier product returned no body')
      return response.data.data
    },
    // The product's own "who sells this" list reads the same links.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'directory'] }),
  })
}

export function useRemoveSupplierProduct(companyId: string, supplierId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variantId: string) => {
      await api.DELETE('/directory/suppliers/{supplier}/products/{variantId}', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { supplier: supplierId, variantId } },
      })
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'directory'] }),
  })
}
